import { useCallback, useState } from "react";
import {
  EntryModel,
  EntryType,
  TextData,
  JournalData,
  WorkspaceData,
  BucketData,
} from "lib/models";
import TextEntry from "components/MoveableEntry/TextEntry";
import WorkspaceEntry from "components/MoveableEntry/WorkspaceEntry";
import JournalEntry from "components/MoveableEntry/JournalEntry";
import BucketEntry from "components/MoveableEntry/BucketEntry";
import ContextMenuButton from "./ContextMenuButton";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "lib/state/TextProvider";
import ResizeHandle, { Dimension } from "./ResizeHandle";

const MIN_SIZES_PX: Record<EntryType, { minWidth: number; minHeight: number }> =
  {
    [EntryType.BUCKET]: { minWidth: 300, minHeight: 150 },
    [EntryType.TEXT]: { minWidth: 100, minHeight: 100 },
    [EntryType.WORKSPACE]: { minWidth: 150, minHeight: 100 },
    [EntryType.JOURNAL]: { minWidth: 310, minHeight: 200 },
    [EntryType.JOURNAL_PAGE]: { minWidth: 250, minHeight: 200 },
  };

interface EntryContainerProps {
  entryRefId: string;
  entry: EntryModel;
  isDragging?: boolean;
  isContextMenuOpen?: boolean;
  onContextMenuOpen: (
    entryRefId: string,
    position: { x: number; y: number } | null,
  ) => void;
  onFocusChange: (focused: boolean) => void;
  size: Dimension;
  onResizeChange: (dimensions: { width: number; height: number }) => void;
  onResizeEnd: (dimensions: { width: number; height: number }) => void;
}

function EntryContainer({
  entryRefId,
  entry,
  isDragging,
  isContextMenuOpen,
  onContextMenuOpen,
  onFocusChange,
  size,
  onResizeChange,
  onResizeEnd,
}: EntryContainerProps) {
  const navigate = useNavigate();
  const { state, setWorkspaceNavigation } = useAppContext();

  const [isHovered, setIsHovered] = useState(false);

  const getComponentForEntry = useCallback(() => {
    switch (entry.data.type) {
      case EntryType.TEXT: {
        if (entryRefId == "tempEntry") {
          console.log("entry container");
        }
        return (
          <TextEntry refId={entryRefId} model={entry as EntryModel<TextData>} />
        );
      }
      case EntryType.WORKSPACE: {
        return (
          <WorkspaceEntry
            entryRefId={entryRefId}
            model={entry as EntryModel<WorkspaceData>}
          />
        );
      }
      case EntryType.JOURNAL: {
        return (
          <JournalEntry
            refId={entryRefId}
            model={entry as EntryModel<JournalData>}
          />
        );
      }
      case EntryType.BUCKET: {
        return (
          <BucketEntry
            refId={entryRefId}
            model={entry as EntryModel<BucketData>}
          />
        );
      }
    }
  }, [entry, entryRefId]);

  const handleDoubleClick = useCallback(
    (_: any) => {
      switch (entry.data.type) {
        case EntryType.TEXT: {
          navigate(`/${EntryType.TEXT}/${entry.uuid}`, { replace: true });
          break;
        }
        case EntryType.WORKSPACE: {
          setWorkspaceNavigation([
            ...state.workspaceNavigation,
            { uuid: entry.uuid, refId: entryRefId, title: entry.data.name },
          ]);
          break;
        }
      }
    },
    [entry, entryRefId],
  );

  const handleRightClick = useCallback((e: any) => {
    e.preventDefault();
  }, []);

  const handleFocus = useCallback(() => {
    onFocusChange(true);
  }, [onFocusChange]);

  const handleBlur = useCallback(() => {
    onFocusChange(false);
  }, [onFocusChange]);

  const focusBorderColor = isDragging ? "ring-blue-100" : "ring-blue-200";
  const focusStyle = `focus:ring-2 focus:${focusBorderColor}`;
  const focusWithinStyle = `focus-within:ring-2 focus-within:${focusBorderColor}`;
  const focusStyles = `${focusStyle} ${focusWithinStyle}`;

  return (
    <div
      tabIndex={0} /** allow focusing */
      className={`absolute shadow-lg rounded-lg border border-2 border-palette-4 overflow:hidden ${focusStyles}`}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleRightClick}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        width: `${size.width}px`,
        height: `${size.height}px`,
        minWidth: `${MIN_SIZES_PX[entry.data.type].minWidth}px`,
        minHeight: `${MIN_SIZES_PX[entry.data.type].minHeight}px`,
      }}
    >
      <ContextMenuButton
        entryRefId={entryRefId}
        onContextMenuOpen={onContextMenuOpen}
        isVisible={!!((isHovered && !isDragging) || isContextMenuOpen)}
      />
      <div className="w-full h-full overflow-auto break-words">
        {getComponentForEntry()}
      </div>
      <ResizeHandle
        size={size}
        onResizeChange={onResizeChange}
        onResizeEnd={onResizeEnd}
        isVisible={isHovered && !isDragging && !isContextMenuOpen}
      />
    </div>
  );
}

export default EntryContainer;
